import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Copy, Check, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
}

export const ShareDialog = ({ open, onOpenChange, chatId }: ShareDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [copied, setCopied] = useState(false);
  
  // Share settings
  const [enablePassword, setEnablePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [enableExpiration, setEnableExpiration] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date>();

  const handleCreateShare = async () => {
    setLoading(true);
    try {
      // Hash password if enabled
      let passwordHash = null;
      if (enablePassword && password) {
        // Simple hash for demo - in production use proper hashing
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      }

      // Create share record
      const { data, error } = await supabase
        .from('chat_shares')
        .insert({
          chat_id: chatId,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          password_hash: passwordHash,
          expires_at: enableExpiration && expirationDate ? expirationDate.toISOString() : null,
        })
        .select()
        .single();

      if (error) throw error;

      // Generate share link with configurable base URL using share_token
      const baseUrl = import.meta.env.VITE_PUBLIC_URL || `${window.location.protocol}//${window.location.host}`;
      const link = `${baseUrl}/shared/${data.share_token}`;
      setShareLink(link);

      toast({
        title: t("share.created"),
        description: t("share.linkReady"),
      });
    } catch (error) {
      console.error('Failed to create share:', error);
      toast({
        title: t("share.error"),
        description: t("share.failedToCreate"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast({
      title: t("share.copied"),
      description: t("share.linkCopied"),
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setShareLink("");
    setPassword("");
    setEnablePassword(false);
    setEnableExpiration(false);
    setExpirationDate(undefined);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("share.title")}</DialogTitle>
          <DialogDescription>{t("share.description")}</DialogDescription>
        </DialogHeader>

        {!shareLink ? (
          <div className="space-y-4 py-4">
            {/* Password Protection */}
            <div className="flex items-center justify-between space-x-2">
              <Label htmlFor="password-toggle" className="flex flex-col space-y-1">
                <span>{t("share.passwordProtection")}</span>
                <span className="font-normal text-xs text-muted-foreground">
                  {t("share.passwordDesc")}
                </span>
              </Label>
              <Switch
                id="password-toggle"
                checked={enablePassword}
                onCheckedChange={setEnablePassword}
              />
            </div>

            {enablePassword && (
              <div className="space-y-2">
                <Label htmlFor="password">{t("share.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("share.enterPassword")}
                />
              </div>
            )}

            {/* Expiration */}
            <div className="flex items-center justify-between space-x-2">
              <Label htmlFor="expiration-toggle" className="flex flex-col space-y-1">
                <span>{t("share.expiration")}</span>
                <span className="font-normal text-xs text-muted-foreground">
                  {t("share.expirationDesc")}
                </span>
              </Label>
              <Switch
                id="expiration-toggle"
                checked={enableExpiration}
                onCheckedChange={setEnableExpiration}
              />
            </div>

            {enableExpiration && (
              <div className="space-y-2">
                <Label>{t("share.expirationDate")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expirationDate && "text-muted-foreground"
                      )}
                    >
                      {expirationDate ? format(expirationDate, "PPP") : t("share.pickDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expirationDate}
                      onSelect={setExpirationDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("share.shareLink")}</Label>
              <div className="flex gap-2">
                <Input value={shareLink} readOnly className="flex-1" />
                <Button size="icon" onClick={handleCopyLink} variant="outline">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {enablePassword && (
              <p className="text-sm text-muted-foreground">
                {t("share.passwordRequired")}
              </p>
            )}
            {enableExpiration && expirationDate && (
              <p className="text-sm text-muted-foreground">
                {t("share.expiresOn").replace("{date}", format(expirationDate, "PPP"))}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {!shareLink ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t("share.cancel")}
              </Button>
              <Button 
                onClick={handleCreateShare} 
                disabled={loading || (enablePassword && !password) || (enableExpiration && !expirationDate)}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("share.create")}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} className="w-full">
              {t("share.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
